import {NextPage} from 'next';

import {
  FileImageVariant,
  MeasurementUnit,
  UpdateUserProfileVariables,
  useEditProfileQuery,
  useUpdateUserProfile,
} from '#apollo/schema';
import {Screen} from '#component/base';
import {EditProfileForm} from '#component/form';
import {AvatarImageInput} from '#component/partial';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {useToastQueue} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {formatAPIDateForInput} from '#util';
import {convertKilogramToPound} from '#util/weight';

export const EditProfileScreen = () => {
  const {addToast} = useToastQueue();
  const {data, loading} = useEditProfileQuery({
    variables: {imageVariant: FileImageVariant.Size_160x160},
  });
  const {userProfile, avatar} = data?.me || {};
  const {
    birthSex,
    birthday,
    firstName,
    height,
    formattedHeight,
    heightDisplayUnit,
    initials,
    lastName,
    pronouns,
    weight,
    weightDisplayUnit,
  } = userProfile || {};
  const [updateUserProfile] = useUpdateUserProfile();

  const handleSubmitUserProfile = async (input: UpdateUserProfileVariables['input']) => {
    const {errors} = await updateUserProfile({variables: {input}});

    if (!errors) {
      addToast({children: 'Profile updated!'});
    }
  };

  return (
    <Screen
      loading={loading}
      contentContainerClassName={sprinkles({flex: 'flex', direction: 'col', gap: 32, pT: [24, 32]})}
      pageName="Edit Profile">
      <AvatarImageInput image={avatar?.url} text={initials || '??'} />

      <EditProfileForm
        birthSex={birthSex}
        birthday={birthday ? formatAPIDateForInput(birthday) : ''}
        firstName={firstName}
        height={heightDisplayUnit === MeasurementUnit.Metric ? height : formattedHeight}
        heightDisplayUnit={heightDisplayUnit}
        lastName={lastName}
        onSubmit={handleSubmitUserProfile}
        pronouns={pronouns}
        weight={
          weightDisplayUnit === MeasurementUnit.Metric
            ? weight
            : Math.round(convertKilogramToPound(weight))
        }
        weightDisplayUnit={weightDisplayUnit}
      />
    </Screen>
  );
};

export const EditProfile: NextPage = withRequireUserState(EditProfileScreen, {
  userState: 'verified',
});
